/**
 * The Photoreal render window's body: how Blender renders the room
 * (scripts/blender/RENDER-SETTINGS.md), on tabs — the Trusscore finishes,
 * the camera's lens, the light from outside (sky or an HDRI from the render
 * library), effects, and output — with the Render button at the foot.
 *
 * The settings are this browser's (photoRender.ts keeps them) and go with
 * every export as `manifest.render`; nothing here changes the 3D view itself,
 * which keeps its own look (the other render windows).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, FileDown, FileUp, Trash2 } from 'lucide-react';
import {
  CONTRAST_LOOKS, FACTORY_PHOTO_RENDER, FOCAL_PRESETS, HDRI_NAME, LAMP_FIXTURES, LENS_TYPES, LensType, OUTPUT_ASPECTS, OutputAspect, PANO_WIDTHS, PLACEMENT_FACTORY, PhotoRenderSettings, RANGES, RangeKey, TEXTURE_SURFACES, TexturePlacement, TextureSurface, VIEW_TRANSFORMS, hasPanorama,
  outputSize, photoRenderFromFile, photoRenderToFile,
} from '../../ui/room3d/photoRender';
import {
  HdriEntry, LIBRARY_CHANGED_EVENT, ServiceDownError, TextureSet, getLibrary, listHdri, listTextures, removeHdri, removeTexture, saveFinishes, textureMapUrl,
} from '../../props/studioApi';
import { FINISH_MAX, type FinishPools as FinishPoolsMap } from '../../ui/room3d/renderFinish';
import type { PlanFinish } from '../../../calculations/plan/floorPlan';
import type { RenderManifest } from '../../ui/room3d/sceneExport';
import { SPACE_TYPES } from '../../../data/presets';
import { NOT_PREVIEWED } from '../../ui/room3d/photoPreview';
import { getViewLens, mmForView, onLensChange } from '../../ui/room3d/lensPreview';
import { DESIGNER_STRINGS as S } from '../strings';
import { Group } from './Group';
import { MiniSlider, TintRow, at, fmt, withValue } from './controls';
import { TextureStudio, itemsFor } from './TextureStudio';
import { HdriFinder, TextureFinder } from './LibraryFinders';
import { TexturePicker } from './TexturePicker';

const P = S.photo;

type Tab = 'materials' | 'textures' | 'camera' | 'lighting' | 'effects' | 'output';
const TABS: Tab[] = ['materials', 'textures', 'camera', 'lighting', 'effects', 'output'];
const TAB_KEY = 'designer.photoRender.tab';

export { withValue };

interface RowsProps {
  settings: PhotoRenderSettings;
  set: (path: string, value: unknown) => void;
  /** What Stage drew for the render (a floor set, an HDRI): shown where it fills a setting left open. */
  staged?: PlanFinish;
  /** The 3D view's look (the room export's manifest.look): the studio's swatches are lit by it, as the render is. */
  look?: RenderManifest['look'];
  /**
   * The Floors by space and HDRIs by space pools (the older finishes, drawn by
   * Stage). Off in the Look Lab, where the look being made is itself a space's:
   * its floor and HDRI are chosen above, for the space picked there.
   */
  bySpace?: boolean;
}


/** A slider with its value; double-click the value (or the ↺) for the factory value. */
const Slider: React.FC<RowsProps & { path: string; label: string; hint?: string; range: RangeKey; step: number; unit?: string }> = ({ settings, set, path, label, hint, range, step, unit }) => {
  const value = at(settings, path) as number;
  const factory = at(FACTORY_PHOTO_RENDER, path) as number;
  const [lo, hi] = RANGES[range];
  const changed = Math.abs(value - factory) > 1e-9;
  return (
    <div className="rc-row">
      <div className="rc-row-head">
        <span className="rc-label" title={hint}>{label}</span>
        {changed && <button type="button" className="rc-reset" title={S.render.resetValue} aria-label={`${S.render.resetValue}: ${label}`} onClick={() => set(path, factory)}>↺</button>}
        <span className="rc-value" onDoubleClick={() => set(path, factory)}>{fmt(value, step)}{unit ?? ''}</span>
      </div>
      <input className="rc-range" type="range" min={lo} max={hi} step={step} value={value} aria-label={label} onChange={(e) => set(path, parseFloat(e.target.value))} />
    </div>
  );
};

const Switch: React.FC<RowsProps & { path: string; label: string; hint?: string }> = ({ settings, set, path, label, hint }) => (
  <div className="rc-row">
    <div className="rc-row-head">
      <span className="rc-label" title={hint}>{label}</span>
      <input className="rc-switch" type="checkbox" aria-label={label} checked={!!at(settings, path)} onChange={(e) => set(path, e.target.checked)} />
    </div>
  </div>
);

function Choice<T extends string>({ settings, set, path, label, hint, options }: RowsProps & { path: string; label: string; hint?: string; options: { value: T; label: string }[] }) {
  return (
    <div className="rc-row">
      <div className="rc-row-head">
        <span className="rc-label" title={hint}>{label}</span>
        <select className="rc-select" aria-label={label} value={at(settings, path) as string} onChange={(e) => set(path, e.target.value)}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
}


/** One Trusscore finish: roughness, specular, clear coat. */
const Finish: React.FC<RowsProps & { which: 'wcb' | 'slatwall' | 'trim'; title: string; note?: string; children?: React.ReactNode }> = ({ which, title, note, children, ...rows }) => (
  <Group id={`photo.finish.${which}`} title={title} note={note}>
    <Slider {...rows} path={`materials.${which}.roughness`} label={P.roughness} hint={P.roughnessHint} range="finish.roughness" step={0.01} />
    <Slider {...rows} path={`materials.${which}.specular`} label={P.specular} hint={P.specularHint} range="finish.specular" step={0.01} />
    <Slider {...rows} path={`materials.${which}.coat`} label={P.coat} hint={P.coatHint} range="finish.coat" step={0.01} />
    <Slider {...rows} path={`materials.${which}.coatRoughness`} label={P.coatRoughness} hint={P.coatRoughnessHint} range="finish.coatRoughness" step={0.01} />
    {children}
  </Group>
);

// ── Tabs ────────────────────────────────────────────────────────────────────

/** A texture set's size, feet, as the pickers show it. */
const setSize = (t: TextureSet) => P.textureSize(t.dimensions[0] / 304.8, t.dimensions[1] / 304.8);

/** The surfaces' pickers and the texture library: sets fetched from Poly Haven, chosen per surface (materials.textures). */

// ── Finishes by space ──────────────────────────────────────────────────────

const SPACE_KEY = 'designer.photoRender.finishSpace';
type FinishEntry = Record<string, string | number>;
/** Each kind's entry: which setting names the set or file, and the settings each entry carries. */
const FINISH_SHAPE: Record<'floor' | 'hdri', { id: string; idPath: string; placement: boolean; fields: [string, RangeKey, number][] }> = {
  // The floor's entries are texture placements, as the Floor picker holds one (materials.textures.floor).
  floor: { id: 'id', idPath: 'materials.textures.floor', placement: true, fields: [['scale', 'textures.scale', 0.05], ['rotation', 'textures.rotation', 1], ['roughness', 'textures.roughness', 0.01], ['relief', 'textures.relief', 0.01], ['brightness', 'textures.brightness', 0.01]] },
  hdri: { id: 'file', idPath: 'light.hdri', placement: false, fields: [['strength', 'light.hdriStrength', 0.05], ['rotation', 'light.hdriRotation', 1]] },
};

/**
 * The floor sets (or the HDRIs) each kind of space may be rendered with, each
 * entry with its own settings (renderFinish.ts): Stage draws one for the
 * plan's space. Kept in the props library through Prop Studio's back end.
 * Add keeps the window's current choice and settings as an entry; Try puts an
 * entry back into the window; Save writes the space's entries to the library.
 */
const FinishPools: React.FC<RowsProps & { which: 'floor' | 'hdri'; options: { value: string; label: string }[]; sets?: TextureSet[]; onEdit?: (entry: TexturePlacement, replace: (next: TexturePlacement | null) => void) => void; saveRef?: React.MutableRefObject<((placement: TexturePlacement, space: string) => void) | null> }> = ({ settings, set, which, options, sets, onEdit, saveRef }) => {
  const shape = FINISH_SHAPE[which];
  const [pools, setPools] = useState<FinishPoolsMap | null>(null);
  const [space, setSpace] = useState<string>(() => { try { return window.localStorage.getItem(SPACE_KEY) || 'garage'; } catch { return 'garage'; } });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const say = (err: unknown) => setError(err instanceof ServiceDownError ? P.hdriNoBackEnd : err instanceof Error ? err.message : String(err));
  useEffect(() => {
    let stale = false;
    getLibrary().then((lib) => { if (!stale) { setPools(lib.finishes ?? {}); setError(null); } }).catch((err) => { if (!stale) { setPools({}); say(err); } });
    return () => { stale = true; };
  }, []);
  const chooseSpace = (s: string) => { setSpace(s); try { window.localStorage.setItem(SPACE_KEY, s); } catch { /* not kept — fine */ } };
  const listOf = (p: FinishPoolsMap | null, s: string): FinishEntry[] => (p?.[s]?.[which] ?? []) as unknown as FinishEntry[];
  const entries = listOf(pools, space);
  const update = (list: FinishEntry[]) => {
    setPools((p) => ({ ...(p ?? {}), [space]: { floor: p?.[space]?.floor ?? [], hdri: p?.[space]?.hdri ?? [], [which]: list } as FinishPoolsMap[string] }));
    setDirty(true);
    setNote(null);
  };
  // The studio's Save to pool: the placement appended to a space's entries (unsaved until Save, as an Add is).
  if (saveRef) {
    saveRef.current = (placement, toSpace) => {
      setPools((p) => ({ ...(p ?? {}), [toSpace]: { floor: [...(p?.[toSpace]?.floor ?? []), placement], hdri: p?.[toSpace]?.hdri ?? [] } as FinishPoolsMap[string] }));
      setDirty(true);
      setNote(null);
    };
  }
  const add = () => {
    if (shape.placement) {
      // The placement as the picker holds it, or the library's first set as published.
      const placed = at(settings, shape.idPath) as TexturePlacement | null;
      const id = placed?.id ?? options[0]?.value;
      if (!id) return;
      update([...entries, { ...PLACEMENT_FACTORY, ...(placed ?? {}), id } as unknown as FinishEntry]);
      return;
    }
    const id = (at(settings, shape.idPath) as string | null) ?? options[0]?.value;
    if (!id) return;
    const entry: FinishEntry = { [shape.id]: id };
    for (const [key, path] of shape.fields) entry[key] = at(settings, path) as number;
    update([...entries, entry]);
  };
  const tryEntry = (e: FinishEntry) => {
    if (shape.placement) {
      const placed: Record<string, string | number> = { id: e.id };
      for (const [key] of shape.fields) placed[key] = Number(e[key] ?? (PLACEMENT_FACTORY as unknown as Record<string, number>)[key]);
      placed.tint = typeof e.tint === 'string' ? e.tint : PLACEMENT_FACTORY.tint;
      placed.tintStrength = Number(e.tintStrength ?? PLACEMENT_FACTORY.tintStrength);
      set(shape.idPath, placed);
      return;
    }
    set(shape.idPath, e[shape.id]);
    if (which === 'hdri') set('light.world', 'hdri');
    for (const [key, path] of shape.fields) set(path, e[key]);
  };
  const save = async () => {
    if (!pools) return;
    setBusy(true);
    try {
      // This kind's entries over the library's as it is now (the other kind's are left as saved there).
      const lib = await getLibrary();
      const merged: FinishPoolsMap = {};
      for (const s of new Set([...Object.keys(lib.finishes ?? {}), ...Object.keys(pools)])) {
        merged[s] = { floor: lib.finishes?.[s]?.floor ?? [], hdri: lib.finishes?.[s]?.hdri ?? [], [which]: pools[s]?.[which] ?? [] } as FinishPoolsMap[string];
      }
      const saved = await saveFinishes(merged);
      setPools(saved);
      setDirty(false);
      setNote(P.finishSaved);
      setError(null);
      window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
    } catch (err) { say(err); } finally { setBusy(false); }
  };
  const label = (key: string) => (P.finishField as Record<string, string>)[key] ?? key;
  return (
    <Group id={`photo.pools.${which}`} defaultOpen={false} title={which === 'floor' ? P.finishFloorGroup : P.finishHdriGroup} note={which === 'floor' ? P.finishFloorNote : P.finishHdriNote}>
      <div className="rc-row">
        <div className="rc-row-head">
          <span className="rc-label">{P.finishSpace}</span>
          <select className="rc-select" aria-label={P.finishSpace} value={space} onChange={(e) => chooseSpace(e.target.value)}>
            {SPACE_TYPES.map((s) => <option key={s.id} value={s.id}>{s.label}{listOf(pools, s.id).length ? ` (${listOf(pools, s.id).length})` : ''}</option>)}
          </select>
        </div>
      </div>
      {pools === null && <p className="rc-note">{P.finishLoading}</p>}
      {pools && entries.length === 0 && <p className="rc-note">{P.finishNone}</p>}
      {entries.map((e, i) => (
        <div key={i} style={{ border: '1px solid rgba(255,255,255,0.14)', borderRadius: 6, padding: '4px 6px', marginTop: 6 }}>
          <div className="rc-row-head" style={{ gap: 4 }}>
            {shape.placement && sets ? (
              <TexturePicker
                sets={sets} value={String(e[shape.id])} label={`${P.finishEntry} ${i + 1}`} style={{ flex: 1 }}
                onChange={(id) => { if (id) update(entries.map((x, j) => (j === i ? { ...x, [shape.id]: id } : x))); }}
              />
            ) : (
              <select
                className="rc-select" style={{ flex: 1, minWidth: 0 }} aria-label={`${P.finishEntry} ${i + 1}`} value={String(e[shape.id])}
                onChange={(ev) => update(entries.map((x, j) => (j === i ? { ...x, [shape.id]: ev.target.value } : x)))}
              >
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                {!options.some((o) => o.value === e[shape.id]) && <option value={String(e[shape.id])}>{String(e[shape.id])}</option>}
              </select>
            )}
            {shape.placement && onEdit && (
              <button type="button" className="rc-action" style={{ flex: '0 0 auto' }} title={S.textureStudio.editHint} onClick={() => onEdit({ ...PLACEMENT_FACTORY, ...(e as unknown as TexturePlacement) }, (next) => update(next ? entries.map((x, j) => (j === i ? (next as unknown as FinishEntry) : x)) : entries.filter((_, j) => j !== i)))}>{S.textureStudio.edit}</button>
            )}
            <button type="button" className="rc-action" style={{ flex: '0 0 auto' }} title={P.finishTryHint} onClick={() => tryEntry(e)}>{P.finishTry}</button>
            <button type="button" className="rc-icon-btn" title={P.finishRemoveEntry} aria-label={P.finishRemoveEntry} onClick={() => update(entries.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
          </div>
          {shape.fields.map(([key, range, step]) => (
            <MiniSlider key={key} label={label(key)} value={Number(e[key] ?? (PLACEMENT_FACTORY as unknown as Record<string, number>)[key] ?? 0)} range={range} step={step} onChange={(v) => update(entries.map((x, j) => (j === i ? { ...x, [key]: v } : x)))} />
          ))}
          {shape.placement && (
            <TintRow
              tint={typeof e.tint === 'string' ? e.tint : PLACEMENT_FACTORY.tint} strength={Number(e.tintStrength ?? PLACEMENT_FACTORY.tintStrength)}
              labels={{ tint: label('tint'), strength: label('tintStrength') }} hints={{ tint: S.textureStudio.fieldHints.tint, strength: S.textureStudio.fieldHints.tintStrength }}
              onChange={(tint, tintStrength) => update(entries.map((x, j) => (j === i ? { ...x, tint, tintStrength } : x)))}
            />
          )}
        </div>
      ))}
      <div className="rc-row-head" style={{ gap: 4, marginTop: 8 }}>
        <button type="button" className="rc-action" disabled={!pools || !options.length || entries.length >= FINISH_MAX} title={which === 'floor' ? P.finishAddFloorHint : P.finishAddHdriHint} onClick={add}>{P.finishAdd}</button>
        <button type="button" className="rc-action" disabled={!dirty || busy} onClick={save}>{busy ? P.finishSaving : P.finishSave}</button>
      </div>
      {note && <p className="rc-note">{note}</p>}
      {error && <p className="rc-note is-bad">{error}</p>}
    </Group>
  );
};

const TextureLibrary: React.FC<RowsProps> = ({ settings, set, staged, look, bySpace = true }) => {
  const [items, setItems] = useState<TextureSet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const chosen = settings.materials.textures;

  const say = (err: unknown) => setError(err instanceof ServiceDownError ? P.hdriNoBackEnd : err instanceof Error ? err.message : String(err));
  const refresh = useCallback(async () => {
    try {
      setItems(await listTextures());
      setError(null);
    } catch (err) {
      setItems([]);
      say(err);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id: string) => {
    if (!window.confirm(P.textureRemoveConfirm(id))) return;
    setBusy(id);
    try {
      await removeTexture(id);
      for (const k of TEXTURE_SURFACES) if (chosen[k]?.id === id) set(`materials.textures.${k}`, null);
      await refresh();
    } catch (err) { say(err); } finally { setBusy(null); }
  };

  const pickers: { key: TextureSurface; label: string; hint?: string }[] = [
    { key: 'lawn', label: P.textureLawn, hint: P.textureLawnHint },
    { key: 'wood', label: P.textureWood, hint: P.textureWoodHint },
    { key: 'fence', label: P.textureFence },
    { key: 'lumber', label: P.textureLumber },
    { key: 'frame', label: P.textureFrame, hint: P.textureFrameHint },
    { key: 'floor', label: P.textureFloor, hint: P.textureFloorHint },
    { key: 'slab', label: P.textureSlab, hint: P.textureSlabHint },
    { key: 'drywall', label: P.textureDrywall, hint: P.textureDrywallHint },
  ];
  // A picker's choice: the set, laid on as before (the placement's numbers kept when only the set changes).
  const choose = (key: TextureSurface, id: string) => set(`materials.textures.${key}`, id ? { ...PLACEMENT_FACTORY, ...(chosen[key] ?? {}), id } : null);
  // The studio: open on a picker's surface (Apply goes into the settings), or on a Floors by space entry (Apply replaces it).
  const [studio, setStudio] = useState<{ surface: TextureSurface; initial: TexturePlacement | null; item?: string; apply: (next: TexturePlacement | null) => void } | null>(null);
  const openStudio = (key: TextureSurface, item = '') => setStudio({ surface: key, initial: chosen[key], item, apply: (next) => set(`materials.textures.${key}`, next) });
  // An item's own placement (materials.items): set, or taken away (the surface's applies again).
  const applyItem = (item: string, next: TexturePlacement | null) => {
    const items = { ...settings.materials.items };
    if (next) items[item] = next; else delete items[item];
    set('materials.items', items);
  };
  const itemsUnder = (key: TextureSurface) => itemsFor(key).filter((id) => settings.materials.items[id]);
  const setName = (id: string) => (items ?? []).find((t) => t.id === id)?.name ?? id;
  const poolSaves = useRef<((placement: TexturePlacement, space: string) => void) | null>(null);
  // The floor's "none": the staging's draw, when there is one, fills it at export.
  const noneLabel = (key: TextureSurface) => (key === 'floor' && staged?.floor ? P.finishStagedFloor(staged.floor.id, staged.floor.scale) : P.textureNone);
  const tile = (key: string, thumb: string | null | undefined, label: string, sub: string, onClick: () => void, extra?: React.ReactNode) => (
    <div key={key} style={{ position: 'relative' }}>
      <button
        type="button" className="rc-tile" onClick={onClick} title={`${label} · ${sub}`}
        style={{ backgroundImage: thumb ? `url(${thumb})` : undefined }}
      >
        <span className="rc-tile-name">{label} <span style={{ opacity: 0.7 }}>{sub}</span></span>
      </button>
      {extra}
    </div>
  );

  return (
    <>
      <Group id="photo.texturesGroup" title={P.texturesGroup} note={P.texturesNote}>
        {pickers.map(({ key, label, hint }) => (
          <div className="rc-row" key={key}>
            <div className="rc-row-head">
              <span className="rc-label" title={hint}>{label}</span>
              <TexturePicker sets={items ?? []} value={chosen[key]?.id ?? ''} noneLabel={noneLabel(key)} label={label} onChange={(id) => choose(key, id)} />
              <button type="button" className="rc-action" style={{ flex: '0 0 auto' }} title={S.textureStudio.editHint} aria-label={`${S.textureStudio.edit} ${label}`} disabled={!(items ?? []).length && !chosen[key]} onClick={() => openStudio(key)}>{S.textureStudio.edit}</button>
            </div>
            {chosen[key] && (chosen[key]?.scale !== 1 || chosen[key]?.rotation !== 0 || chosen[key]?.roughness !== 1 || chosen[key]?.relief !== 1 || chosen[key]?.brightness !== 1 || (chosen[key]?.tintStrength ?? 0) > 0) && (
              <p className="rc-note" style={{ marginTop: 2 }}>{S.textureStudio.placed(chosen[key] as TexturePlacement)}</p>
            )}
            {itemsUnder(key).map((id) => (
              <div className="rc-row-head" key={id} style={{ gap: 4, marginTop: 2, paddingLeft: 10 }}>
                <span className="rc-label" style={{ fontSize: 11 }} title={S.textureStudio.placed(settings.materials.items[id])}>↳ {(S.openingStyleName as Record<string, string>)[id] ?? id}: {setName(settings.materials.items[id].id)}</span>
                <button type="button" className="rc-action" style={{ flex: '0 0 auto', height: 22 }} title={S.textureStudio.editHint} aria-label={`${S.textureStudio.edit} ${(S.openingStyleName as Record<string, string>)[id] ?? id}`} onClick={() => openStudio(key, id)}>{S.textureStudio.edit}</button>
              </div>
            ))}
          </div>
        ))}
      </Group>
      {studio && (
        <TextureStudio
          surface={studio.surface} initial={studio.initial} initialItem={studio.item} sets={items ?? []} settings={settings}
          onApply={studio.apply} onApplyItem={applyItem} look={look}
          onSaveToPool={studio.surface === 'floor' && poolSaves.current ? (placement, space) => poolSaves.current?.(placement, space) : undefined}
          onClose={() => setStudio(null)}
        />
      )}
      <Group id="photo.textureFind" title={P.textureFind} note={P.textureFindNote}>
        <TextureFinder onFetched={() => { refresh(); }} />
      </Group>
      <Group id="photo.textureLibrary" defaultOpen={false} title={P.textureLibrary} note={P.textureLibraryNote}>
        {items === null && <p className="rc-note">{P.textureLoading}</p>}
        {items && items.length === 0 && !error && <p className="rc-note">{P.textureEmpty}</p>}
        {error && <p className="rc-note is-bad">{error}</p>}
        {items && items.length > 0 && (
          <div className="rc-grid">
            {items.map((t) => tile(t.id, t.thumbnail ?? (t.maps.diffuse ? textureMapUrl(t.id, t.maps.diffuse) : null), t.name, setSize(t), () => undefined, (
              <button
                type="button" className="rc-icon-btn" title={P.textureRemove(t.id)} aria-label={P.textureRemove(t.id)} disabled={busy === t.id}
                onClick={() => remove(t.id)} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.55)', borderRadius: 4 }}
              >
                <Trash2 size={12} />
              </button>
            )))}
          </div>
        )}
      </Group>
      {bySpace && (
        <FinishPools
          settings={settings} set={set} which="floor" options={(items ?? []).map((t) => ({ value: t.id, label: `${t.name} · ${setSize(t)}` }))} sets={items ?? []}
          onEdit={(entry, replace) => setStudio({ surface: 'floor', initial: entry, apply: replace })}
          saveRef={poolSaves}
        />
      )}
    </>
  );
};

const MaterialsTab: React.FC<RowsProps & { seamStrength: number }> = ({ seamStrength, ...rows }) => {
  const joints = rows.settings.materials.wcb.joints;
  return (
    <>
      <Finish {...rows} which="wcb" title={P.wcb}>
        <div className="rc-row">
          <div className="rc-row-head">
            <span className="rc-label" title={P.jointsHint}>{P.jointsFollow(seamStrength.toFixed(2))}</span>
            <input className="rc-switch" type="checkbox" aria-label={P.jointsFollowLabel} checked={joints === null} onChange={(e) => rows.set('materials.wcb.joints', e.target.checked ? null : seamStrength)} />
          </div>
        </div>
        {joints !== null && <Slider {...rows} path="materials.wcb.joints" label={P.joints} hint={P.jointsHint} range="materials.wcb.joints" step={0.01} />}
      </Finish>
      <Finish {...rows} which="slatwall" title={P.slatwall} />
      <Finish {...rows} which="trim" title={P.trim} />
      <Group id="photo.floor" title={P.floor}>
        <Slider {...rows} path="materials.floor.roughness" label={P.roughness} hint={P.roughnessHint} range="materials.floor.roughness" step={0.01} />
        <Slider {...rows} path="materials.floor.relief" label={P.relief} hint={P.reliefHint} range="materials.floor.relief" step={0.01} />
      </Group>
    </>
  );
};

/** The 3D view's own focal length (mm, full-frame), as it reports it (lensPreview.ts); '' until it has. */
function useViewMm(): string {
  const read = () => {
    const v = getViewLens();
    return v ? String(Math.round(mmForView(v.fov, v.aspect))) : '';
  };
  const [mm, setMm] = useState(read);
  useEffect(() => onLensChange(() => setMm(read())), []);
  return mm;
}

/** The lens: its type, and (normal or fisheye) its focal length -- the view's own, a preset, or typed. */
const LensGroup: React.FC<RowsProps> = ({ settings, set }) => {
  const { focalMm, lensType } = settings.camera;
  const viewMm = useViewMm();
  const [lo, hi] = RANGES['camera.focalMm'];
  const [draft, setDraft] = useState(focalMm === null ? '' : String(focalMm));
  useEffect(() => { setDraft(focalMm === null ? '' : String(focalMm)); }, [focalMm]);
  const preset = focalMm === null ? 'view' : FOCAL_PRESETS.includes(focalMm) ? String(focalMm) : 'other';
  const commit = () => {
    const n = Number(draft);
    if (!draft.trim() || !Number.isFinite(n)) { setDraft(focalMm === null ? '' : String(focalMm)); return; }
    const v = Math.round(Math.min(hi, Math.max(lo, n)) * 10) / 10;
    setDraft(String(v));
    if (v !== focalMm) set('camera.focalMm', v);
  };
  return (
    <Group id="photo.cameraLensGroup" title={P.cameraLensGroup} note={P.cameraLensNote}>
      <div className="rc-row">
        <div className="rc-row-head">
          <span className="rc-label" title={P.lensTypeHint}>{P.lensType}</span>
          <select className="rc-select" aria-label={P.lensType} value={lensType} onChange={(e) => set('camera.lensType', e.target.value as LensType)}>
            {LENS_TYPES.map((t) => <option key={t} value={t}>{P.lensTypes[t]}</option>)}
          </select>
        </div>
      </div>
      {(
        <div className="rc-row">
          <div className="rc-row-head" style={{ gap: 4 }}>
            <span className="rc-label" title={P.focalHint}>{lensType === 'fisheye' ? P.focalFisheye : P.focal}</span>
            <select
              className="rc-select" aria-label={P.focal} value={preset} style={{ width: 120 }}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'view') set('camera.focalMm', null);
                else if (v === 'other') set('camera.focalMm', focalMm ?? 50);
                else set('camera.focalMm', Number(v));
              }}
            >
              <option value="view">{lensType === 'fisheye' ? P.focalFisheyeView : P.focalView(viewMm)}</option>
              {FOCAL_PRESETS.map((f) => <option key={f} value={String(f)}>{f} mm</option>)}
              <option value="other">{P.focalOther}</option>
            </select>
            {focalMm !== null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input
                  className="rc-select" type="number" min={lo} max={hi} step={1} aria-label={`${P.focal} mm`} value={draft} style={{ width: 64, textAlign: 'right' }}
                  onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
                />
                <span style={{ fontSize: 12, opacity: 0.75 }}>mm</span>
              </span>
            )}
          </div>
        </div>
      )}
      {lensType === 'fisheye' && <p className="rc-note">{P.fisheyeNote}</p>}
      {lensType === 'perspective' && focalMm !== null && <p className="rc-note">{P.previewLens}</p>}
    </Group>
  );
};

const CameraTab: React.FC<RowsProps> = (rows) => {
  const { camera } = rows.settings;
  return (
    <>
      <LensGroup {...rows} />
      <Group id="photo.cornerGroup" title={P.cornerGroup} note={P.cornerNote}>
        <Slider {...rows} path="camera.cornerVaryFt" label={P.cornerVary} hint={P.cornerVaryHint} range="camera.cornerVaryFt" step={0.5} unit=" ft" />
        <Switch {...rows} path="camera.cornerClear" label={P.cornerClear} hint={P.cornerClearHint} />
      </Group>
      <Group id="photo.dofGroup" title={P.dofGroup} note={P.dofNote}>
        <Switch {...rows} path="camera.dof" label={P.dof} hint={P.dofHint} />
        {camera.dof && (
          <>
            <Slider {...rows} path="camera.fStop" label={P.fStop} hint={P.fStopHint} range="camera.fStop" step={0.05} />
            <div className="rc-row">
              <div className="rc-row-head">
                <span className="rc-label" title={P.autoFocusHint}>{P.autoFocus}</span>
                <input className="rc-switch" type="checkbox" aria-label={P.autoFocus} checked={camera.focusFt === null} onChange={(e) => rows.set('camera.focusFt', e.target.checked ? null : 10)} />
              </div>
            </div>
            {camera.focusFt !== null && <Slider {...rows} path="camera.focusFt" label={P.focus} hint={P.focusHint} range="camera.focusFt" step={0.1} unit=" ft" />}
          </>
        )}
      </Group>
      {camera.dof && (
        <Group id="photo.bokehGroup" title={P.bokehGroup} note={P.bokehNote}>
          <Slider {...rows} path="camera.bokehBlades" label={P.blades} hint={P.bladesHint} range="camera.bokehBlades" step={1} />
          <Slider {...rows} path="camera.bokehRotation" label={P.bladeRotation} range="camera.bokehRotation" step={1} unit="°" />
          <Slider {...rows} path="camera.bokehRatio" label={P.bokehRatio} hint={P.bokehRatioHint} range="camera.bokehRatio" step={0.01} />
        </Group>
      )}
    </>
  );
};

/** The HDRI library: what is there, Poly Haven's free ones, and a file of your own. */
const HdriLibrary: React.FC<RowsProps> = ({ settings, set, bySpace = true }) => {
  const [items, setItems] = useState<HdriEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const chosen = settings.light.hdri;

  const say = (err: unknown) => setError(err instanceof ServiceDownError ? P.hdriNoBackEnd : err instanceof Error ? err.message : String(err));
  const refresh = useCallback(async () => {
    try {
      setItems(await listHdri());
      setError(null);
    } catch (err) {
      setItems([]);
      say(err);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const choose = (file: string) => { set('light.hdri', file); set('light.world', 'hdri'); };
  const remove = async (file: string) => {
    if (!window.confirm(P.hdriRemoveConfirm(file))) return;
    setBusy(file);
    try {
      await removeHdri(file);
      if (chosen === file) { set('light.hdri', null); set('light.world', 'sky'); }
      await refresh();
    } catch (err) { say(err); } finally { setBusy(null); }
  };

  const tile = (key: string, thumb: string | null | undefined, label: string, on: boolean, onClick: () => void, extra?: React.ReactNode) => (
    <div key={key} style={{ position: 'relative' }}>
      <button
        type="button" className={`rc-tile is-wide${on ? ' is-active' : ''}`} onClick={onClick} title={label} aria-pressed={on}
        style={{ backgroundImage: thumb ? `url(${thumb})` : undefined }}
      >
        <span className="rc-tile-name">{label}</span>
      </button>
      {extra}
    </div>
  );

  return (
    <>
    <Group id="photo.hdriLibrary" defaultOpen={false} title={P.hdriLibrary} note={P.hdriLibraryNote}>
      {items === null && <p className="rc-note">{P.hdriLoading}</p>}
      {items && items.length === 0 && !error && <p className="rc-note">{P.hdriEmpty}</p>}
      {items && items.length > 0 && (
        <div className="rc-grid is-2">
          {items.map((h) => tile(h.file, h.thumbnail, h.name || h.file, h.file === chosen, () => choose(h.file), (
            <button
              type="button" className="rc-icon-btn" title={P.hdriRemove(h.file)} aria-label={P.hdriRemove(h.file)} disabled={busy === h.file}
              onClick={() => remove(h.file)} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.55)', borderRadius: 4 }}
            >
              <Trash2 size={12} />
            </button>
          )))}
        </div>
      )}
      <HdriFinder onFetched={async (entry) => { await refresh(); choose(entry.file); }} />
      {error && <p className="rc-note is-bad">{error}</p>}
      {chosen && !HDRI_NAME.test(chosen) && <p className="rc-note is-bad">{chosen}</p>}
    </Group>
    {bySpace && <FinishPools settings={settings} set={set} which="hdri" options={(items ?? []).map((h) => ({ value: h.file, label: h.name }))} />}
    </>
  );
};

/** The view outside: a ground-projected backdrop seen through the windows and doors only (RENDER-SETTINGS.md). */
const ViewOutside: React.FC<RowsProps> = (rows) => {
  const { light } = rows.settings;
  const { view } = light;
  const [files, setFiles] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    listHdri().then((items) => { if (live) setFiles(items.map((h) => h.file)); }).catch(() => { if (live) setFiles([]); });
    return () => { live = false; };
  }, [light.hdri]);
  const options = [
    { value: '', label: light.hdri ? P.viewSameAsLighting(light.hdri) : P.viewSameAsLightingNone },
    ...files.map((f) => ({ value: f, label: f })),
  ];
  return (
    <Group id="photo.viewGroup" defaultOpen={false} title={P.viewGroup} note={P.viewNote}>
      <Switch {...rows} path="light.view.enabled" label={P.viewEnabled} hint={P.viewEnabledHint} />
      {view.enabled && (
        <>
          <div className="rc-row">
            <div className="rc-row-head">
              <span className="rc-label" title={P.viewImageHint}>{P.viewImage}</span>
              <select className="rc-select" style={{ maxWidth: 190 }} aria-label={P.viewImage} value={view.image ?? ''} onChange={(e) => rows.set('light.view.image', e.target.value || null)}>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {!view.image && !light.hdri && <p className="rc-note is-bad">{P.viewNoImage}</p>}
          <Slider {...rows} path="light.view.heightFt" label={P.viewHeight} hint={P.viewHeightHint} range="light.view.heightFt" step={0.1} unit=" ft" />
          <Slider {...rows} path="light.view.radiusFt" label={P.viewRadius} hint={P.viewRadiusHint} range="light.view.radiusFt" step={10} unit=" ft" />
          <div className="rc-row">
            <div className="rc-row-head">
              <span className="rc-label" title={P.viewRotationFollowHint}>{P.viewRotationFollow}</span>
              <input className="rc-switch" type="checkbox" aria-label={P.viewRotationFollow} checked={view.rotation === null} onChange={(e) => rows.set('light.view.rotation', e.target.checked ? null : light.hdriRotation)} />
            </div>
          </div>
          {view.rotation !== null && <Slider {...rows} path="light.view.rotation" label={P.viewRotation} range="light.view.rotation" step={1} unit="°" />}
          <Slider {...rows} path="light.view.strength" label={P.viewStrength} range="light.view.strength" step={0.05} />
        </>
      )}
    </Group>
  );
};

const LightingTab: React.FC<RowsProps> = (rows) => {
  const { light } = rows.settings;
  return (
    <>
      <Group id="photo.outside" title={P.outside}>
        <Choice {...rows} path="light.world" label={P.world} hint={P.worldHint} options={[
          { value: 'sky', label: P.worldSky },
          { value: 'hdri', label: light.hdri ? P.worldHdri(light.hdri) : P.worldHdriNone },
        ]} />
        {rows.staged?.hdri && !light.hdri && <p className="rc-note">{P.finishStagedHdri(rows.staged.hdri.file)}</p>}
        {light.world === 'sky' && <Slider {...rows} path="light.skyStrength" label={P.skyStrength} range="light.skyStrength" step={0.05} />}
        {light.world === 'hdri' && light.hdri && (
          <>
            <Slider {...rows} path="light.hdriStrength" label={P.hdriStrength} range="light.hdriStrength" step={0.05} />
            <Slider {...rows} path="light.hdriRotation" label={P.hdriRotation} hint={P.hdriRotationHint} range="light.hdriRotation" step={1} unit="°" />
            <Switch {...rows} path="light.hdriVisible" label={P.hdriVisible} hint={P.hdriVisibleHint} />
          </>
        )}
      </Group>
      <HdriLibrary {...rows} />
      <ViewOutside {...rows} />
      <Group id="photo.sunGroup" title={P.sunGroup}>
        <Choice {...rows} path="light.sun" label={P.sun} hint={P.sunHint} options={[
          { value: 'auto', label: P.sunAuto }, { value: 'on', label: P.sunOn }, { value: 'off', label: P.sunOff },
        ]} />
        {light.sun !== 'off' && (
          <>
            <Slider {...rows} path="light.sunStrength" label={P.sunStrength} range="light.sunStrength" step={0.1} />
            <Slider {...rows} path="light.sunElevation" label={P.sunElevation} hint={P.sunElevationHint} range="light.sunElevation" step={1} unit="°" />
            <Slider {...rows} path="light.sunSwing" label={P.sunSwing} hint={P.sunSwingHint} range="light.sunSwing" step={1} unit="°" />
            <Slider {...rows} path="light.sunSoftness" label={P.sunSoftness} hint={P.sunSoftnessHint} range="light.sunSoftness" step={0.1} unit="°" />
          </>
        )}
      </Group>
      <Group id="photo.lampsGroup" title={P.lampsGroup}>
        <Choice {...rows} path="light.lamps.fixture" label={P.lampFixture} hint={P.lampFixtureHint} options={LAMP_FIXTURES.map((f) => ({ value: f, label: S.render.lights.fixtures[f] }))} />
        <Slider {...rows} path="light.lamps.spacingFt" label={P.lampSpacing} hint={P.lampSpacingHint} range="light.lamps.spacingFt" step={0.5} unit=" ft" />
        <Slider {...rows} path="light.lampScale" label={P.lampScale} hint={P.lampScaleHint} range="light.lampScale" step={0.05} unit="×" />
        <Slider {...rows} path="light.lamps.warmth" label={P.lampWarmth} hint={P.lampWarmthHint} range="light.lamps.warmth" step={50} unit=" K" />
        <Switch {...rows} path="light.lamps.fixtures" label={P.lampFixtures} hint={P.lampFixturesHint} />
      </Group>
    </>
  );
};

const EffectsTab: React.FC<RowsProps> = (rows) => (
  <>
    <Group id="photo.toneGroup" title={P.toneGroup}>
      <Switch {...rows} path="effects.autoExposure" label={P.autoExposure} hint={P.autoExposureHint} />
      <Slider {...rows} path="effects.exposure" label={P.exposure} hint={rows.settings.effects.autoExposure ? P.exposureHintMetered : P.exposureHint} range="effects.exposure" step={0.05} unit=" EV" />
      <Choice {...rows} path="effects.view" label={P.view} hint={P.viewHint} options={VIEW_TRANSFORMS.map((v) => ({ value: v, label: v }))} />
      {rows.settings.effects.view === 'AgX' && (
        <Choice {...rows} path="effects.contrast" label={P.contrast} options={CONTRAST_LOOKS.map((v) => ({ value: v, label: v }))} />
      )}
      <Slider {...rows} path="effects.saturation" label={P.saturation} range="effects.saturation" step={0.01} />
    </Group>
    <Group id="photo.shadingGroup" title={P.shadingGroup}>
      <Slider {...rows} path="effects.contact" label={P.contact} hint={P.contactHint} range="effects.contact" step={0.05} unit="×" />
    </Group>
    <Group id="photo.lensGroup" title={P.lensGroup} note={P.lensNote}>
      <Slider {...rows} path="effects.bloom" label={P.bloom} hint={P.bloomHint} range="effects.bloom" step={0.01} />
      <Slider {...rows} path="effects.vignette" label={P.vignette} range="effects.vignette" step={0.01} />
      <Slider {...rows} path="effects.dispersion" label={P.dispersion} hint={P.dispersionHint} range="effects.dispersion" step={0.01} />
      <Slider {...rows} path="effects.softness" label={P.softness} hint={P.softnessHint} range="effects.softness" step={0.01} />
      <Slider {...rows} path="effects.grain" label={P.grain} hint={P.grainHint} range="effects.grain" step={0.01} />
    </Group>
  </>
);

/** The picture's width, typed: set on Enter or when the box is left, pulled into range. */
const WidthBox: React.FC<RowsProps> = ({ settings, set }) => {
  const width = settings.output.width;
  const [draft, setDraft] = useState(String(width));
  useEffect(() => { setDraft(String(width)); }, [width]);
  const [lo, hi] = RANGES['output.width'];
  const commit = () => {
    const n = Math.round(Number(draft));
    if (!draft.trim() || !Number.isFinite(n)) { setDraft(String(width)); return; }
    const v = Math.min(hi, Math.max(lo, n));
    setDraft(String(v));
    if (v !== width) set('output.width', v);
  };
  return (
    <div className="rc-row">
      <div className="rc-row-head">
        <span className="rc-label" title={P.widthHint}>{P.width}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <input
            className="rc-select" type="number" inputMode="numeric" min={lo} max={hi} step={1} aria-label={P.width} title={P.widthHint}
            style={{ width: 84, textAlign: 'right' }} value={draft}
            onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
          />
          <span style={{ fontSize: 12, opacity: 0.75 }}>px</span>
        </span>
      </div>
    </div>
  );
};

/** The picture's shape: one of the list, ticked. */
const AspectList: React.FC<RowsProps> = ({ settings, set }) => {
  const chosen = settings.output.aspect;
  return (
    <div className="rc-row">
      <div className="rc-row-head"><span className="rc-label" title={P.aspectHint}>{P.aspect}</span></div>
      <div role="radiogroup" aria-label={P.aspect} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '2px 10px', marginTop: 4 }}>
        {OUTPUT_ASPECTS.map((a: OutputAspect) => (
          <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', gridColumn: a === 'view' ? '1 / -1' : undefined }}>
            <input type="radio" name="photo-output-aspect" value={a} checked={chosen === a} onChange={() => set('output.aspect', a)} />
            {a === 'view' ? P.aspectView : a}
          </label>
        ))}
      </div>
      <p className="rc-note" style={{ marginTop: 4 }}>
        {settings.output.aspect === 'view' ? P.sizeFollowsView(settings.output.width) : P.size(...outputSize(settings, 1))}
      </p>
    </div>
  );
};

const OutputTab: React.FC<RowsProps> = (rows) => (
  <Group id="photo.outputGroup" title={P.outputGroup}>
    <Slider {...rows} path="output.samples" label={P.samples} hint={P.samplesHint} range="output.samples" step={16} />
    <Switch {...rows} path="output.denoise" label={P.denoise} hint={P.denoiseHint} />
    <WidthBox {...rows} />
    <AspectList {...rows} />
    <Choice {...rows} path="output.views" label={P.views} options={[
      { value: 'user', label: P.viewsUser }, { value: 'all', label: P.viewsAll },
      { value: 'pano', label: P.viewsPano }, { value: 'all+pano', label: P.viewsAllPano },
    ]} />
    {hasPanorama(rows.settings.output.views) && (
      <>
        <div className="rc-row">
          <div className="rc-row-head">
            <span className="rc-label" title={P.panoWidthHint}>{P.panoWidth}</span>
            <select
              className="rc-select" aria-label={P.panoWidth} value={rows.settings.output.panoWidth}
              onChange={(e) => rows.set('output.panoWidth', Number(e.target.value))}
            >
              {PANO_WIDTHS.map((w) => <option key={w} value={w}>{P.panoWidths[w]}</option>)}
            </select>
          </div>
        </div>
        <p className="rc-note">{P.panoNote}</p>
      </>
    )}
  </Group>
);

/** Every setting to a .json file, downloaded. */
function downloadConfig(s: PhotoRenderSettings): void {
  const url = URL.createObjectURL(new Blob([photoRenderToFile(s)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'photoreal-render-settings.json';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// ── The window body ─────────────────────────────────────────────────────────

export interface PhotoRenderBodyProps {
  settings: PhotoRenderSettings;
  onChange: (next: PhotoRenderSettings) => void;
  /** The look's seam Strength: what the board joints follow until set here. */
  seamStrength: number;
  /** Save the room with these settings for the renderer; resolves to a message to show, if any. */
  onRender?: () => Promise<string | void>;
  /** The Render button's own words (the Look Lab's is a test render). */
  renderLabel?: string;
  /** These settings shown, roughly, in the live 3D view (photoPreview.ts). */
  preview?: boolean;
  onPreview?: (on: boolean) => void;
  /** What Stage drew for the render (a floor set, an HDRI), shown where it applies. */
  staged?: PlanFinish;
  /** The 3D view's look, for the studio's swatches. */
  look?: RenderManifest['look'];
  /** Show the Floors by space and HDRIs by space pools (off in the Look Lab). */
  bySpace?: boolean;
}

export const PhotoRenderBody: React.FC<PhotoRenderBodyProps> = ({ settings, onChange, seamStrength, onRender, renderLabel, preview = false, onPreview, staged, look, bySpace = true }) => {
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const t = window.localStorage.getItem(TAB_KEY) as Tab | null;
      return t && TABS.includes(t) ? t : 'materials';
    } catch { return 'materials'; }
  });
  useEffect(() => { try { window.localStorage.setItem(TAB_KEY, tab); } catch { /* not kept — fine */ } }, [tab]);
  const [note, setNote] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const configInput = useRef<HTMLInputElement>(null);
  const importConfig = async (file: File) => {
    try {
      const next = photoRenderFromFile(await file.text());
      latest.current = next;
      onChange(next);
      let missing: string | null = null;
      if (next.light.hdri) {
        try {
          const library = await listHdri();
          if (!library.some((h) => h.file === next.light.hdri)) missing = next.light.hdri;
        } catch { /* no back end to ask: say nothing */ }
      }
      let missingSets: string[] = [];
      const wanted = TEXTURE_SURFACES.map((k) => next.materials.textures[k]?.id).filter((id): id is string => !!id);
      if (wanted.length) {
        try {
          const sets = await listTextures();
          missingSets = [...new Set(wanted.filter((id) => !sets.some((t) => t.id === id)))];
        } catch { /* no back end to ask: say nothing */ }
      }
      setNote([P.importedConfig(file.name), missing ? P.importMissingHdri(missing) : '', missingSets.length ? P.importMissingTexture(missingSets.join(', ')) : ''].filter(Boolean).join(' '));
    } catch (err) {
      setNote(err instanceof Error && err.message === 'not-json' ? P.importNotJson(file.name) : P.importNotSettings(file.name));
    }
  };

  // One change at a time, each on top of the last (sliders fire fast).
  const latest = useRef(settings);
  latest.current = settings;
  const set = useCallback((path: string, value: unknown) => {
    const next = withValue(latest.current, path, value);
    latest.current = next;
    onChange(next);
  }, [onChange]);
  const rows = { settings, set, staged, look, bySpace };

  return (
    <div className="rc-pane">
      <div className="rc-pane-tabs">
        <div className="rc-mode" role="tablist" aria-label={P.title}>
          {TABS.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`rc-mode-btn${tab === t ? ' is-active' : ''}`} onClick={() => setTab(t)}>
              {P.tabs[t]}
            </button>
          ))}
        </div>
        {onPreview && (
          <div className="rc-row" style={{ padding: '6px 0 0' }}>
            <div className="rc-row-head">
              <span className="rc-label" title={P.previewHint}>{P.preview}</span>
              <input className="rc-switch" type="checkbox" aria-label={P.preview} checked={preview} onChange={(e) => onPreview(e.target.checked)} />
            </div>
          </div>
        )}
      </div>
      <div className="rc-pane-body">
      {onPreview && preview && <p className="rc-note" style={{ marginTop: 6 }}>{P.previewNote(NOT_PREVIEWED.join(', '))}</p>}
      {tab === 'materials' && <MaterialsTab {...rows} seamStrength={seamStrength} />}
      {tab === 'textures' && <TextureLibrary {...rows} />}
      {tab === 'camera' && <CameraTab {...rows} />}
      {tab === 'lighting' && <LightingTab {...rows} />}
      {tab === 'effects' && <EffectsTab {...rows} />}
      {tab === 'output' && <OutputTab {...rows} />}
      </div>
      {onRender && (
        <div className="rc-pane-foot">
          <button
            type="button" className="rc-action is-active" disabled={rendering} title={P.renderTitle}
            style={{ width: '100%', justifyContent: 'center', height: 34, fontSize: 13, fontWeight: 700 }}
            onClick={async () => {
              setRendering(true);
              setNote(null);
              try { setNote((await onRender()) || P.rendered); } catch (err) { setNote(err instanceof Error ? err.message : String(err)); } finally { setRendering(false); }
            }}
          >
            <Camera size={15} /> {rendering ? P.rendering : renderLabel ?? P.render}
          </button>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button type="button" className="rc-action" style={{ flex: 1, justifyContent: 'center' }} title={P.exportConfigTitle} onClick={() => downloadConfig(latest.current)}>
              <FileDown size={13} /> {P.exportConfig}
            </button>
            <button type="button" className="rc-action" style={{ flex: 1, justifyContent: 'center' }} title={P.importConfigTitle} onClick={() => configInput.current?.click()}>
              <FileUp size={13} /> {P.importConfig}
            </button>
            <input
              ref={configInput} type="file" accept=".json,application/json" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importConfig(f); }}
            />
          </div>
          {note && <p className="rc-note" style={{ marginTop: 6 }}>{note}</p>}
        </div>
      )}
    </div>
  );
};

export default PhotoRenderBody;
